import { getQueryClient } from '@/lib/query/client/query-client';
import { useQuery } from '@tanstack/react-query';

import { storageLocationsApi } from './storage-locations.api';
import type { StorageLocationsListParams } from '../types';
import { STALE_TIME } from '@/lib/query/stale-times';

export const storageLocationKeys = {
  all: ['storageLocations'] as const,
  lists: () => [...storageLocationKeys.all, 'list'] as const,
  list: (filters?: StorageLocationsListParams) =>
    [...storageLocationKeys.lists(), filters] as const,
  details: () => [...storageLocationKeys.all, 'detail'] as const,
  detail: (id: number) => [...storageLocationKeys.details(), id] as const,
  stats: () => [...storageLocationKeys.all, 'stats'] as const
};

export const storageLocationQueries = {
  list: (filters?: StorageLocationsListParams) => ({
    queryKey: storageLocationKeys.list(filters),
    queryFn: () => storageLocationsApi.getAll(filters)
  }),

  detail: (id: number) => ({
    queryKey: storageLocationKeys.detail(id),
    queryFn: () => storageLocationsApi.getById(id),
    enabled: id > 0
  }),

  stats: () => ({
    queryKey: storageLocationKeys.stats(),
    queryFn: () => storageLocationsApi.getStats(),
    // `stats`, not `metrics`: StorageLocationService caches this under
    // `cache_ttl.stats` (120s), so a 60s refetch just re-reads the same payload.
    staleTime: STALE_TIME.stats
  })
};

export const useStorageLocations = (filters?: StorageLocationsListParams) => {
  return useQuery({
    ...storageLocationQueries.list(filters),
    placeholderData: (previousData) => previousData
  });
};

export const useStorageLocation = (id: number) => {
  return useQuery(storageLocationQueries.detail(id));
};

export const useStorageLocationStats = () => {
  return useQuery(storageLocationQueries.stats());
};

export const prefetchStorageLocations = (
  filters?: StorageLocationsListParams
) => {
  return getQueryClient().prefetchQuery(storageLocationQueries.list(filters));
};
